'use client';

import Link from 'next/link';
import { JSX, useEffect, useState } from 'react';

import { Tooltip } from '@videoblocks/design-system';

import { BUSINESS_PLAN, SMALL_BUSINESS_PLAN, getPlanTypeFromCode } from '../../utils/planUtils';
import { useCheckoutContext } from './CheckoutContext';
import { PlanFeatures } from './PlanFeatures';
import PlanSelectionBox from './PlanSelectionBox';
import { usePurchasePreview } from './usePurchasePreview';

export default function PlanPricePreview(): JSX.Element {
    const { monthlyPlan, yearlyPlan, planCode, setPlanCode, promoCode, isPromoCodeEnabled } = useCheckoutContext();
    const { currencySymbol, subtotal, tax, discount, total } = usePurchasePreview();
    const [renewalDate, setRenewalDate] = useState<Date | null>(null);

    const isYearly = (): boolean => {
        return planCode === yearlyPlan?.code;
    };

    const getLicenseText = (): string => {
        const planType = getPlanTypeFromCode(planCode);
        if (planType === BUSINESS_PLAN || planType === SMALL_BUSINESS_PLAN) {
            return 'Business License Included';
        }
        return 'Individual License Included';
    };

    const getLicenseLink = (): string => {
        const planType = getPlanTypeFromCode(planCode);
        if (planType === BUSINESS_PLAN || planType === SMALL_BUSINESS_PLAN) {
            return '/license/small-business-license';
        }
        return '/license/individual-license';
    };

    // Show promo discount when discount is applied (determined by Recurly)
    // Promo code stays in context regardless of plan selection
    // If plan is ineligible, Recurly returns discount=0 and we hide the line
    const showPromoDiscount = isPromoCodeEnabled && !!promoCode && !!discount && discount > 0;

    // Calculate renewal date on client side only to avoid hydration issues
    useEffect(() => {
        const date = new Date();
        const isYearly = planCode === yearlyPlan?.code;
        if (isYearly) {
            date.setFullYear(date.getFullYear() + 1);
        } else {
            date.setMonth(date.getMonth() + 1);
        }
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRenewalDate(date);
    }, [planCode, yearlyPlan?.code]);

    const handlePlanSelection = (selectedPlanCode: string): void => {
        setPlanCode(selectedPlanCode);
        // Promo code stays in context - eligibility is determined by Recurly
    };

    return (
        <div id="pricingPreview" className="w-full h-full flex flex-col space-y-6 text-gray-900">
            {/* Plan title */}
            <div className="text-left">
                <h2 className="text-xl font-bold">{isYearly() ? yearlyPlan?.name : monthlyPlan?.name}</h2>
                <Link
                    className="text-sm font-medium text-blue-700 hover:underline"
                    href={getLicenseLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {getLicenseText()}
                </Link>
            </div>

            {/* Plan options */}
            <div className="flex flex-col w-full border-b border-gray-300 space-y-4 text-sm pb-4">
                {yearlyPlan?.code && (
                    <PlanSelectionBox
                        isSelected={isYearly()}
                        title="Annual Plan"
                        plan={yearlyPlan}
                        onClick={handlePlanSelection}
                    />
                )}
                {monthlyPlan?.code && (
                    <PlanSelectionBox
                        isSelected={!isYearly()}
                        title="Monthly Plan"
                        plan={monthlyPlan}
                        onClick={handlePlanSelection}
                    />
                )}
                {/* Plan features */}
                <div className="space-y-3">
                    <div className="text-sm text-gray-900">
                        <PlanFeatures plan={planCode} />
                    </div>
                </div>
            </div>

            {/* Pricing breakdown */}
            <div className="space-y-5">
                <div className="space-y-5 text-sm">
                    <div className="flex justify-between">
                        <span className="font-medium">Subtotal</span>
                        <span>
                            {currencySymbol}
                            {subtotal.toFixed(2)}
                        </span>
                    </div>
                    {/* Promo discount only appears when discount > 0 */}
                    {showPromoDiscount && (
                        <div className="flex justify-between text-sm text-green-600 font-semibold">
                            <span>Promo Applied ({promoCode?.toUpperCase()})</span>
                            <span>
                                ({currencySymbol}
                                {discount.toFixed(2)})
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span className="font-medium">
                            Estimated sales tax
                            <Tooltip
                                title="Storyblocks is required to collect sales tax in certain tax jurisdictions per local law and regulations. Please contact customer support if you are a tax-exempt business."
                                trigger={
                                    <span className="inline-block align-middle ml-1">
                                        <svg
                                            className="w-4 h-4 text-gray-600 cursor-pointer"
                                            viewBox="0 0 20 20"
                                            tabIndex={0}
                                        >
                                            <circle cx="10" cy="10" r="9" fill="currentColor" />
                                            <text
                                                x="10"
                                                y="15"
                                                textAnchor="middle"
                                                fontSize="12"
                                                fontWeight="bold"
                                                fill="#fff"
                                                fontFamily="Arial, sans-serif"
                                            >
                                                ?
                                            </text>
                                        </svg>
                                    </span>
                                }
                            />
                        </span>
                        <span>
                            {currencySymbol}
                            {tax.toFixed(2)}
                        </span>
                    </div>
                    <div className="flex justify-between border-b pb-5">
                        <span className="font-medium">Total</span>
                        <span>
                            {currencySymbol}
                            {total.toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Final purchase info */}
            <div className="space-y-3 text-sm">
                <div className="flex justify-between items-end">
                    <span className="font-medium">Payment Due</span>
                    <span className="text-3xl font-bold text-gray-900">
                        {currencySymbol}
                        {total.toFixed(2)}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="font-medium">Renewal Date</span>
                    <span className="font-semibold">
                        {renewalDate
                            ? renewalDate.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                              })
                            : '...'}
                    </span>
                </div>
                <div className="flex items-center justify-end">
                    <svg className="w-6 h-6 mr-1 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <span className="text-black font-semibold">Cancel Anytime</span>
                </div>
            </div>
        </div>
    );
}
